//@ts-strict-ignore
import isPromise from '../../../utils/isPromise';
import { serviceActions } from '../../../state/modules/service';
import { getMutationType } from '../../../graphql/schema/getQueryFields';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import { change, customValidation, mergeConflicts } from './actions';
import { client } from '../../../client';
import { handleAlerts, parseErrorResponse } from '../../../utils/parseErrorResponse';
import * as entities from '../../../graphql/entities';
import getIn from '../../../components/connectedForm/utils/getIn';
import ConcurrencyUtils from '../../../utils/ConcurrencyUtils';
import getClientMutationId from '../../../utils/getClientMutationId';
import { AlertType, Maybe } from '../../../graphql/schema/p4types';
import { IActionType } from '../Hooks/usePerformSave';
import { useP4ToastController } from '../../../views/system/NotificationsArea';

/**
 * @param props                  - Form Props (Not the biggest fan of doing this, but needed for various linked functions).
 * @param fields                 - Registered fields against the connected form (For validation purposes).
 * @param saveOrDelete           - Confirmation string for save (Also used to tag 'deleted' into values when appropriate).
 * @param suppressSuccessMessage - True will prevent success message ONLY
 * @param suppressAfterSubmit    - Prevent 'AfterSubmit' logic, because we intend to follow it up with another action (E.g. Opening a form like dispatch, or posting an invoice).
 * @returns {*}
 */
const handleSubmit = (
  props,
  fields,
  saveOrDelete: Maybe<'saved' | 'deleted' | 'completed' | 'archived'> = 'saved',
  { suppressSuccessMessage = false, suppressAfterSubmit = false } = {}
) => {
  const {
    dispatch,
    entity,
    formUpdate,
    getSubmitValues,
    name,
    onSubmitFailed,
    onSubmitSuccess,
    startSubmit,
    stopSubmit,
    submitFunctions: { submitInitiated }, //Calls this with the clientMutationId for job management.
    setSubmitFailed,
    setSubmitSucceeded,
    stopValidation,
    values,
    passedValues,
    graphql,
    connectedForm,
    supportingData,
    toastProps,
  } = props;

  const { dispatchSimpleToast } = toastProps as ReturnType<typeof useP4ToastController>;

  const doSubmit = () => {
    let type = entity.type
      ? entity.type.charAt(0).toLowerCase() + entity.type.slice(1)
      : entity.charAt(0).toLowerCase() + entity.slice(1);
    const inputType = getMutationType(`${type}UpdateInput`);
    if (inputType) type = inputType;
    const clientMutationId = getClientMutationId();
    submitInitiated(clientMutationId);
    const input = {
      clientMutationId,
    };
    let formValues;
    if (getSubmitValues) {
      formValues = getSubmitValues(props, false, supportingData);
    } else {
      formValues = values;
    }
    const variables = normalizeVariables(formValues);

    if (Array.isArray(type)) {
      for (const objType of type) {
        if (passedValues && {}.hasOwnProperty.call(passedValues, objType)) {
          input[objType] = passedValues[objType];
        } else {
          input[objType] = variables;
        }
      }
    } else {
      input[type] = variables;
    }
    if (saveOrDelete === 'deleted') input[type].deleted = true;
    let result;

    try {
      if (graphql) {
        result = client.mutate({
          mutation: graphql,
          variables: {
            input,
            temporary: false,
          },
        });
      } else {
        result = formUpdate({
          variables: {
            input,
            temporary: false,
          },
        });
      }
    } catch (submitError) {
      const { errors } = submitError;
      const error = parseErrorResponse(errors);

      stopSubmit(error.networkResponse);
      setSubmitFailed(...fields);
      onSubmitFailed?.(errors);
      if (errors) {
        return error.networkResponse;
      }
      throw submitError;
    }
    if (isPromise(result)) {
      startSubmit();
      const submitHandler: (submitResult: {
        data: any;
      }) => { result?: any; action: IActionType } | { error?: any; action: IActionType } = (submitResult) => {
        const { data } = submitResult;
        const response = data[Object.keys(data)[0]];

        if ((saveOrDelete === 'deleted' && response === null) || response?.result) {
          stopSubmit();
          if (!suppressSuccessMessage) {
            const entityMeta = entities && entities[entity];
            const notificationKey = getIn(entityMeta, 'notificationKey');
            const entityRef = notificationKey ? getIn(response, `result.${notificationKey}`) : '';
            dispatchSimpleToast(
              `${name} ${saveOrDelete}`,
              `${name} ${entityRef} was ${saveOrDelete} successfully`,
              AlertType.Success
            );
          }
          if (
            saveOrDelete !== 'deleted' &&
            (response?.result.id || response?.result.entityMetaData || response?.result.uuid)
          ) {
            if (props.onModalSelect) {
              props.onModalSelect(response?.result);
            }
            dispatch(change(props.form, false, response?.result));
          }
          if (onSubmitSuccess) {
            onSubmitSuccess(response?.result, dispatch, props);
          }
          setSubmitSucceeded();

          return { result: response?.result, action: { submitMeta: { suppressSuccessMessage, suppressAfterSubmit } } };
        }
        const error = parseErrorResponse(submitResult);

        handleAlerts(error, props, saveOrDelete);
        stopSubmit(error);
        setSubmitFailed();
        onSubmitFailed?.(error);

        return { error: error, action: { submitMeta: { suppressSuccessMessage, suppressAfterSubmit } } };
      };

      return (result as Promise<{ data: any }>).then(submitHandler, (submitError) => {
        let errors: Array<any> | null = null;

        if (submitError.networkError && submitError.networkError.result) {
          errors = submitError.networkError.result.errors;
        }
        const error = parseErrorResponse(errors);

        stopSubmit(errors);
        setSubmitFailed(...fields);
        onSubmitFailed?.(errors);

        if (error.violationErrors) {
          const violationErrors = {};
          Object.entries(error.violationErrors).forEach(([key, value]) => {
            // if first part of the path matches entity type then remove the prefix and process
            if (key !== '' && props?.entity.type === key.substring(0, key.indexOf('.'))) {
              const newKey = key.substring(key.indexOf('.') + 1);
              violationErrors[newKey] = value;
              props.dispatch(customValidation(props.form, newKey, value));
            }
          });

          stopValidation(violationErrors);
        }
        if (errors?.[0] && errors[0].errorType === 'CONCURRENCY_VIOLATION') {
          if (props.formData && props.formData.refetch) {
            if (props.formData.data?.id) {
              //For an existing record, we need to reload form data & compare.
              console.debug('Reloading form data for conflict resolution.');
              props.formData.refetch().then((data) => {
                const concurrencyUtils = new ConcurrencyUtils();
                const conflicts = concurrencyUtils.compareValues(data.data.data, formValues);

                dispatch(mergeConflicts(props.form, conflicts));
              });
            } else {
              console.warn('Unable to resolve conflicts for new records at this time', errors[0]);
              //TODO - We need an alternate means of identifying conflicts for new records - We cannot simply compare the records since there is no 'previous' record for
              // comparison.
            }
          }
        }
        handleAlerts(error, props, saveOrDelete);
        if (errors?.[0] && errors[0].errorType === 'CONFIRMATION_REQUIRED' && errors[0].data) {
          dispatch(connectedForm.change(false, errors[0].data));
        }
        if (submitError) {
          return submitError;
        }
        throw submitError;
      });
    }
    setSubmitSucceeded();
    if (onSubmitSuccess) {
      onSubmitSuccess(result, dispatch, props);
    }

    return result;
  };

  return doSubmit();
};

export default handleSubmit;
